import { Routes } from '@angular/router';
import { PageLayoutComponent } from './components/layout/page-layout/page-layout.component';
import { routeRoleGuard, routeRoleActivateGuard } from './guards/RouteRoleGuard';
import { ROLES } from './utils/constants';
import { AuthGuard } from './guards/AuthGuard';
import { ErrorPageComponent } from './components/public/error-page/error-page.component';
import { HomeRedirectComponent } from './components/home-redirect/home-redirect.component';

const TENANT_ROLES = [ROLES.admin, ROLES.gestore, ROLES.esperto];

export const routes: Routes = [
  { path: '', canActivate: [AuthGuard], component: HomeRedirectComponent, pathMatch: 'full' },
  {
    path: 'avvisi',
    component: PageLayoutComponent,
    canMatch: [routeRoleGuard(TENANT_ROLES)],
    canActivate: [AuthGuard, routeRoleActivateGuard(TENANT_ROLES)],
    children: [
      {
        path: 'lista',
        loadComponent: () =>
          import(
            './components/application/avvisi-list-page/avvisi-list-page.component'
          ).then((m) => m.AvvisiListPageComponent),
      },
      {
        path: '',
        loadComponent: () =>
          import(
            './components/application/application-container/application-container.component'
          ).then((m) => m.ApplicationContainerComponent),
      },
      {
        path: ':idAvviso',
        loadComponent: () =>
          import(
            './components/application/application-detail/application-detail.component'
          ).then((m) => m.ApplicationDetailComponent),
      },
    ],
  },
  {
    path: 'guida',
    component: PageLayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import(
            './components/shared/help/guida-page.component'
          ).then((m) => m.GuidaPageComponent),
      },
    ],
  },
  {
    path: 'rivalutazione',
    component: PageLayoutComponent,
    canMatch: [routeRoleGuard(TENANT_ROLES)],
    canActivate: [AuthGuard, routeRoleActivateGuard(TENANT_ROLES)],
    children: [
      {
        path: ':idAvviso/:idDomanda/standard',
        loadComponent: () =>
          import(
            './components/evaluation/standard-re-evaluate/standard-re-evaluate.component'
          ).then((m) => m.StandardReEvaluateComponent),
      },
      {
        path: ':idAvviso/:idDomanda',
        loadComponent: () =>
          import(
            './components/evaluation/re-evaluate/re-evaluate.component'
          ).then((m) => m.ReEvaluateComponent),
      },
    ],
  },
  {
    path: 'valutazione',
    component: PageLayoutComponent,
    canMatch: [routeRoleGuard(TENANT_ROLES)],
    canActivate: [AuthGuard, routeRoleActivateGuard(TENANT_ROLES)],
    children: [
      {
        path: ':idAvviso/standard',
        loadComponent: () =>
          import(
            './components/evaluation/evaluation-form/evaluation-form.component'
          ).then((m) => m.EvaluationFormComponent),
      },
      {
        path: ':idAvviso/:idDomanda/standard',
        loadComponent: () =>
          import(
            './components/evaluation/evaluation-form/evaluation-form.component'
          ).then((m) => m.EvaluationFormComponent),
      },
      {
        path: ':idAvviso/:idDomanda',
        loadComponent: () =>
          import(
            './components/evaluation/evaluation-feedback-form/evaluation-feedback-form.component'
          ).then((m) => m.EvaluationFeedbackFormComponent),
      },
      {
        path: ':idAvviso',
        loadComponent: () =>
          import(
            './components/evaluation/evaluation-feedback-form/evaluation-feedback-form.component'
          ).then((m) => m.EvaluationFeedbackFormComponent),
      },
    ],
  },
  {
    path: 'esito',
    component: PageLayoutComponent,
    canMatch: [routeRoleGuard(TENANT_ROLES)],
    canActivate: [AuthGuard, routeRoleActivateGuard(TENANT_ROLES)],
    children: [
      {
        path: ':idAvviso/:idDomanda',
        loadComponent: () =>
          import(
            './components/evaluation/evaluation-result/evaluation-result.component'
          ).then((m) => m.EvaluationResultComponent),
      },
    ],
  },
  {
    path: 'admin',
    component: PageLayoutComponent,
    canMatch: [routeRoleGuard([ROLES.admin])],
    canActivate: [AuthGuard, routeRoleActivateGuard([ROLES.admin])],
    children: [
      {
        path: 'avvisi',
        loadComponent: () =>
          import('./components/admin/avvisi-list/avvisi-list.component').then(
            (m) => m.AvvisiListComponent
          ),
      },
      {
        path: 'nuovo-avviso',
        loadComponent: () =>
          import('./components/admin/nuovo-avviso/nuovo-avviso.component').then(
            (m) => m.NuovoAvvisoComponent
          ),
      },
      {
        path: 'nuovo-avviso/:idAvviso',
        loadComponent: () =>
          import('./components/admin/nuovo-avviso/nuovo-avviso.component').then(
            (m) => m.NuovoAvvisoComponent
          ),
      },
      {
        path: 'avvisi/:idAvviso',
        loadComponent: () =>
          import(
            './components/admin/admin-application-detail/admin-application-detail.component'
          ).then((m) => m.AdminApplicationDetailComponent),
      },
      {
        path: 'criteri/:idAvviso',
        loadComponent: () =>
          import(
            './components/admin/nuovo-criterio/nuovo-criterio.component'
          ).then((m) => m.NuovoCriterioComponent),
      },
      {
        path: 'criteri/:idAvviso/:idCriterio',
        loadComponent: () =>
          import(
            './components/admin/nuovo-criterio/nuovo-criterio.component'
          ).then((m) => m.NuovoCriterioComponent),
      },
    ],
  },
  {
    path: '',
    component: PageLayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: 'analytics',
        canMatch: [routeRoleGuard([ROLES.admin, ROLES.gestore])],
        canActivate: [routeRoleActivateGuard([ROLES.admin, ROLES.gestore])],
        loadComponent: () =>
          import('./components/admin/analytics/analytics.component').then(
            (m) => m.AnalyticsComponent
          ),
      },
    ],
  },
  // --- Ticketing ---
  {
    path: 'ticketing',
    component: PageLayoutComponent,
    canMatch: [routeRoleGuard([ROLES.admin, ROLES.gestore, ROLES.supporto, ROLES.asset])],
    canActivate: [AuthGuard, routeRoleActivateGuard([ROLES.admin, ROLES.gestore, ROLES.supporto, ROLES.asset])],
    children: [
      {
        path: '',
        loadComponent: () =>
          import(
            './components/ticketing/dashboard/ticketing-dashboard.component'
          ).then((m) => m.TicketingDashboardComponent),
      },
      {
        path: 'tickets',
        loadComponent: () =>
          import(
            './components/ticketing/ticket-list/ticket-list.component'
          ).then((m) => m.TicketListComponent),
      },
      {
        path: 'tickets/:id',
        loadComponent: () =>
          import(
            './components/ticketing/ticket-detail/ticket-detail.component'
          ).then((m) => m.TicketDetailComponent),
      },
      {
        path: 'admin/pending',
        canMatch: [routeRoleGuard([ROLES.admin, ROLES.gestore, ROLES.asset])],
        loadComponent: () =>
          import(
            './components/ticketing/admin/pending-tickets/pending-tickets.component'
          ).then((m) => m.PendingTicketsComponent),
      },
      {
        path: 'admin/stats',
        canMatch: [routeRoleGuard([ROLES.admin, ROLES.gestore, ROLES.asset])],
        loadComponent: () =>
          import(
            './components/ticketing/admin/stats-page/stats-page.component'
          ).then((m) => m.StatsPageComponent),
      },
    ],
  },
  // --- Superadmin ---
  {
    path: 'superadmin',
    component: PageLayoutComponent,
    canMatch: [routeRoleGuard([ROLES.superadmin])],
    canActivate: [AuthGuard, routeRoleActivateGuard([ROLES.superadmin])],
    children: [
      {
        path: 'tenants',
        loadComponent: () =>
          import('./components/superadmin/tenant-list/tenant-list.component').then(
            (m) => m.TenantListComponent
          ),
      },
      {
        path: 'tenants/nuovo',
        loadComponent: () =>
          import('./components/superadmin/tenant-form/tenant-form.component').then(
            (m) => m.TenantFormComponent
          ),
      },
      {
        path: 'tenants/:code',
        loadComponent: () =>
          import('./components/superadmin/tenant-form/tenant-form.component').then(
            (m) => m.TenantFormComponent
          ),
      },
      {
        path: 'tenants/:code/utenti',
        loadComponent: () =>
          import('./components/superadmin/user-list/user-list.component').then(
            (m) => m.UserListComponent
          ),
      },
      {
        path: 'tenants/:code/utenti/nuovo',
        loadComponent: () =>
          import('./components/superadmin/user-form/user-form.component').then(
            (m) => m.UserFormComponent
          ),
      },
    ],
  },
  {
    path: 'error',
    component: PageLayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        component: ErrorPageComponent,
      },
    ],
  },
  {
    path: 'errore',
    component: ErrorPageComponent,
  },
  { path: '**', redirectTo: 'errore' },
];
